import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const HmisExports = () => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get(`${API}/hmis/exports`).then((r) => setFiles(r.data.files)).catch(() => {});
  }, []);

  const download = async (file) => {
    setBusy(file);
    try {
      const r = await axios.get(`${API}/hmis/export/${file}`);
      const blob = new Blob([r.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = r.data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${r.data.filename} downloaded`);
    } catch (e) { toast.error('Export failed'); } finally { setBusy(''); }
  };

  return (
    <div className="space-y-6" data-testid="hmis-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="h-6 w-6 text-indigo-600" /> HMIS Reporting Exports</h1>
        <p className="text-sm text-gray-600 mt-1">CSV exports for HMIS upload and CoC reporting. Fields map from your resident records; required-but-uncollected fields default per the CSV spec.</p>
      </div>
      {Object.entries(files.reduce((acc, f) => { (acc[f.category || 'Other'] = acc[f.category || 'Other'] || []).push(f); return acc; }, {})).map(([category, group]) => (
        <div key={category} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{category}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {group.map((f) => (
              <Card key={f.file} className="border-0 card-shadow" data-testid={`hmis-card-${f.file}`}>
                <CardHeader>
                  <CardTitle className="text-lg">{f.name}</CardTitle>
                  <CardDescription>{f.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => download(f.file)} disabled={busy === f.file} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid={`hmis-download-${f.file}`}>
                    <Download className="h-4 w-4 mr-2" /> {busy === f.file ? 'Preparing...' : 'Download CSV'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-500">CICoC exports are aligned to the FY2024 HMIS CSV format used by All Chicago. IL PATH is a client-level PATH data extract; confirm the exact PDX upload package with your HMIS lead before submission.</p>
    </div>
  );
};

export default HmisExports;
